import { ERC20StateChangeItem } from '~/components/txn-display/state-change/types'
import { SupportedChainId } from '~/constants/chains'
import {
  EarnAction,
  IAddress,
  OrgRegularTransaction,
  PublicRegularTransaction,
} from '~/types'
import { parseTransactionMetadata } from '~/utils/parseTransactionMetadata'
import { unwrapToken } from '~/utils/tokens'

/**
 * Builds fallback ERC20 state change items from transaction metadata.
 * Used for failed transactions to show the intended transfers when
 * on-chain logs are unavailable.
 */
export const buildFallbackStateChangeItems = (
  transaction: OrgRegularTransaction | PublicRegularTransaction,
): ERC20StateChangeItem[] => {
  const metadata = parseTransactionMetadata(transaction.transactionMetadata)
  const chainId = transaction.chainId as SupportedChainId
  const address = transaction.smartAccountAddress

  if (metadata.swapData) {
    const { sellTokenAddress, sellAmount, buyTokenAddress, buyAmount } =
      metadata.swapData
    return buildExchangeItems({
      address,
      chainId,
      sellToken: sellTokenAddress as IAddress,
      sellAmount,
      buyToken: buyTokenAddress as IAddress | undefined,
      buyAmount,
    })
  }

  if (metadata.bridgeData) {
    const {
      sellTokenAddress,
      sellAmount,
      buyTokenAddress,
      buyAmount,
      destinationChainId,
    } = metadata.bridgeData
    return buildExchangeItems({
      address,
      chainId,
      sellToken: sellTokenAddress as IAddress,
      sellAmount,
      buyToken: buyTokenAddress as IAddress | undefined,
      buyAmount,
      buyChainId: destinationChainId as SupportedChainId | undefined,
    })
  }

  if (metadata.earnData) {
    const { asset, assets, vault, shares, action } = metadata.earnData
    const earnChainId = (metadata.earnData.chainId ??
      chainId) as SupportedChainId
    const isDeposit = action === EarnAction.DEPOSIT
    return buildExchangeItems({
      address,
      chainId: earnChainId,
      sellToken: (isDeposit ? asset : vault) as IAddress,
      sellAmount: isDeposit ? assets : shares,
      buyToken: (isDeposit ? vault : asset) as IAddress,
      buyAmount: isDeposit ? shares : assets,
    })
  }

  if (metadata.claimData) {
    const { tokenAddress, tokenAmount } = metadata.claimData
    if (tokenAddress && tokenAmount) {
      const claimChainId = (metadata.claimData.chainId ??
        chainId) as SupportedChainId
      const token = unwrapToken(tokenAddress as IAddress)
      if (token) {
        return [
          buildItem({
            direction: 'inflow',
            address,
            chainId: claimChainId,
            token,
            amount: BigInt(tokenAmount),
          }),
        ]
      }
    }
  }

  if (metadata.transferData) {
    const { tokenAddress, recipients } = metadata.transferData
    if (tokenAddress && recipients?.length) {
      const token = unwrapToken(tokenAddress as IAddress)
      if (token) {
        const totalAmount = recipients.reduce(
          (sum, r) => sum + BigInt(r.amount),
          0n,
        )
        return [
          buildItem({
            direction: 'outflow',
            address,
            chainId,
            token,
            amount: totalAmount,
            suffix: 'transfer',
          }),
          ...recipients.map((r, i) =>
            buildItem({
              direction: 'inflow',
              address: r.address,
              chainId,
              token,
              amount: BigInt(r.amount),
              suffix: `transfer-${i}`,
            }),
          ),
        ]
      }
    }
  }

  return []
}

const buildItem = ({
  direction,
  address,
  chainId,
  token,
  amount,
  suffix,
}: {
  direction: 'inflow' | 'outflow'
  address: IAddress
  chainId: SupportedChainId
  token: IAddress
  amount: bigint
  suffix?: string
}): ERC20StateChangeItem => ({
  id: `fallback-${direction}-${chainId}-${token}${suffix ? `-${suffix}` : ''}`,
  state_change_type: 'erc-20',
  address,
  chainId,
  token,
  amount: direction === 'outflow' ? -amount : amount,
  direction,
})

const buildExchangeItems = ({
  address,
  chainId,
  sellToken,
  sellAmount,
  buyToken,
  buyAmount,
  buyChainId,
}: {
  address: IAddress
  chainId: SupportedChainId
  sellToken: IAddress
  sellAmount: string
  buyToken?: IAddress
  buyAmount?: string
  buyChainId?: SupportedChainId
}): ERC20StateChangeItem[] => {
  if (!sellToken || !sellAmount) return []
  const unwrappedSellToken = unwrapToken(sellToken)
  if (!unwrappedSellToken) return []

  const items: ERC20StateChangeItem[] = [
    buildItem({
      direction: 'outflow',
      address,
      chainId,
      token: unwrappedSellToken,
      amount: BigInt(sellAmount),
    }),
  ]

  if (buyToken && buyAmount) {
    const unwrappedBuyToken = unwrapToken(buyToken)
    if (unwrappedBuyToken) {
      items.push(
        buildItem({
          direction: 'inflow',
          address,
          chainId: buyChainId ?? chainId,
          token: unwrappedBuyToken,
          amount: BigInt(buyAmount),
        }),
      )
    }
  }

  return items
}
